import { useState } from "react";
import { feedItems, feedTabs, type FeedItem } from "@/mocks/guatian";

const tabStyle: Record<string, { badge: string; chip: string }> = {
  follow: { badge: "bg-primary-100 text-primary-700", chip: "border-primary-200 text-primary-700" },
  reversal: { badge: "bg-accent-100 text-accent-700", chip: "border-accent-200 text-accent-700" },
  news: { badge: "bg-secondary-100 text-secondary-800", chip: "border-secondary-300 text-secondary-800" },
  hotpot: { badge: "bg-background-200 text-foreground-700", chip: "border-background-300 text-foreground-600" },
};

const stateChip: Record<FeedItem["state"], string> = {
  已证实: "bg-primary-50 text-primary-700 border border-primary-200",
  待证实: "bg-secondary-50 text-secondary-800 border border-secondary-200",
  反转节点: "bg-accent-50 text-accent-700 border border-accent-200",
  快讯: "bg-background-100 text-foreground-600 border border-background-200",
};

const hotRank = [
  { t: "综艺「下架」传闻再反转", v: "9876", up: true },
  { t: "热播剧主创直播回应争议", v: "9321", up: true },
  { t: "主播晒录屏澄清旧账", v: "8810", up: false },
  { t: "品牌代言变动口径不一", v: "7764", up: true },
  { t: "演唱会阵容变动被否认", v: "6542", up: true },
  { t: "伪造截图作者公开致歉", v: "5880", up: false },
];

export default function Feed() {
  const [active, setActive] = useState("all");
  const items = active === "all" ? feedItems : feedItems.filter((f) => f.tab === active);

  return (
    <section id="updates" className="bg-background-50">
      <div className="mx-auto max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-accent-500/10 px-3 py-1.5 text-xs font-bold text-accent-700">
              <span className="gt-pulse h-2 w-2 rounded-full bg-accent-500"></span>
              LIVE 实时追更
            </div>
            <h2 className="mt-3 text-2xl font-bold tracking-tight text-foreground-950 md:text-4xl">
              今日追更 <span className="text-accent-600">动态流</span>
            </h2>
            <p className="mt-2 max-w-xl text-sm leading-6 text-foreground-500">
              编辑部按时间滚动整理当天正在发生与仍在跟进的热点，按栏目分类快速切换。
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-1 rounded-md border border-background-200 bg-background-100 p-1">
            {feedTabs.map((t) => (
              <button
                key={t.key}
                onClick={() => setActive(t.key)}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition whitespace-nowrap ${
                  active === t.key
                    ? "bg-primary-600 text-background-50"
                    : "text-foreground-600 hover:bg-background-50"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_340px]">
          {/* 主动态流 */}
          <div className="flex flex-col divide-y divide-background-200 overflow-hidden rounded-lg border border-background-200 bg-background-50">
            {items.map((f) => {
              const st = tabStyle[f.tab];
              return (
                <article key={f.id} className="group relative flex flex-col gap-3 p-5 transition hover:bg-background-100/60 md:p-6">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className={`inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-bold ${st.badge}`}>
                      <i className="ri-flashlight-fill text-[10px]" aria-hidden="true"></i>
                      {f.badge}
                    </span>
                    <time className="text-xs text-foreground-400">{f.time}</time>
                    <span className={`ml-auto inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs ${stateChip[f.state]}`}>
                      {f.state}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold leading-snug text-foreground-950 transition group-hover:text-primary-700">
                      {f.title}
                    </h3>
                    <p className="mt-2 text-sm leading-6 text-foreground-600">{f.desc}</p>
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex flex-wrap gap-2">
                      {f.tags.map((t) => (
                        <span
                          key={t}
                          className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs ${st.chip}`}
                        >
                          # {t}
                        </span>
                      ))}
                    </div>
                    <span className="inline-flex items-center gap-1 text-xs text-foreground-400">
                      <i className="ri-fire-line text-accent-500" aria-hidden="true"></i>
                      热度 {f.heat}
                      <i className="ri-arrow-right-up-line ml-2 transition group-hover:translate-x-0.5 group-hover:text-primary-600" aria-hidden="true"></i>
                    </span>
                  </div>
                </article>
              );
            })}
          </div>

          {/* 右栏：热搜 + 状态图例 */}
          <aside className="flex flex-col gap-6">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-500 text-background-50">
                  <i className="ri-fire-fill" aria-hidden="true"></i>
                </span>
                <h3 className="text-base font-bold text-foreground-950">热搜实时榜</h3>
                <span className="ml-auto text-xs text-foreground-400">每 30 分钟更新</span>
              </div>
              <ol className="mt-4 space-y-1">
                {hotRank.map((h, i) => (
                  <li key={h.t}>
                    <button className="flex w-full items-center gap-3 rounded-md px-2 py-2.5 text-left transition hover:bg-background-100">
                      <span
                        className={`w-6 text-center font-black ${
                          i < 3 ? "text-accent-600" : "text-foreground-300"
                        }`}
                        style={{ fontFamily: "var(--font-label)" }}
                      >
                        {i + 1}
                      </span>
                      <span className="flex-1 truncate text-sm text-foreground-700">{h.t}</span>
                      <span className="inline-flex items-center gap-0.5 text-xs text-foreground-400">
                        {h.up ? <i className="ri-arrow-up-line text-accent-500" aria-hidden="true"></i> : <i className="ri-arrow-down-line text-foreground-300" aria-hidden="true"></i>}
                        {h.v}
                      </span>
                    </button>
                  </li>
                ))}
              </ol>
            </div>

            <div className="rounded-lg border border-background-200 bg-secondary-100/60 p-5">
              <h3 className="flex items-center gap-2 text-base font-bold text-foreground-950">
                <i className="ri-question-answer-line text-secondary-700" aria-hidden="true"></i>
                三种状态怎么读
              </h3>
              <p className="mt-2 text-xs leading-5 text-foreground-600">
                站内所有信息都明确分层，绝不替你做最终判断：
              </p>
              <ul className="mt-3 space-y-2.5">
                <li className="flex items-center gap-2 text-sm text-foreground-700">
                  <span className="h-2.5 w-2.5 rounded-full bg-primary-500"></span>已证实 · 多方来源可验证
                </li>
                <li className="flex items-center gap-2 text-sm text-foreground-700">
                  <span className="h-2.5 w-2.5 rounded-full bg-secondary-500"></span>待证实 · 仍需官方口径确认
                </li>
                <li className="flex items-center gap-2 text-sm text-foreground-700">
                  <span className="h-2.5 w-2.5 rounded-full bg-accent-500"></span>反转节点 · 方向已出现转向
                </li>
              </ul>
              <a
                href="#subscribe"
                className="mt-4 inline-flex w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-foreground-950 px-4 py-2.5 text-sm font-bold text-background-50 transition hover:bg-foreground-800"
              >
                我有线索要补充
                <i className="ri-send-plane-fill" aria-hidden="true"></i>
              </a>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}